// src/components/logistique/FenetrePhotoCamera.jsx
// ============================================================================
// Prendre la photo d'un actif avec la caméra, dans la page
// ============================================================================
//
// « Prendre une photo » ouvre la caméra elle-même (demande de Daniel du
// 04/11/2026) : un champ fichier avec `capture` n'ouvre l'appareil photo que
// sur un téléphone, et sur un ordinateur il ouvrait le même choix de fichier
// que « Choisir un fichier ». Modèle : la caméra de la Vie scolaire
// (VieScolairePage.jsx) - une fenêtre, l'image en direct, « Capturer ».
//
// La caméra arrière d'un téléphone d'abord (`environment`), sinon celle de
// l'ordinateur. Elle exige une page sécurisée (https, ou localhost) : c'est le
// cas en production. La photo part en JPEG ; ChampPhoto la réduit ensuite
// comme une photo choisie.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { BOUTON_CONTOUR, BOUTON_PIED, BOUTON_PLEIN } from '../ui/boutons';

/** La caméra peut-elle s'ouvrir dans la page ? Sinon, l'appareil photo du téléphone. */
export const cameraDansLaPage = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

const message = (e) => {
  if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
    return "L'accès à la caméra a été refusé. Autorisez-le dans les réglages du navigateur, ou choisissez un fichier.";
  }
  if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
    return "Aucune caméra n'est disponible sur cet appareil. Choisissez un fichier.";
  }
  if (e?.name === 'NotReadableError') {
    return "La caméra est déjà utilisée par une autre application. Fermez-la, puis réessayez.";
  }
  return "La caméra n'a pas pu s'ouvrir. Réessayez, ou choisissez un fichier.";
};

const FenetrePhotoCamera = ({ ouvert, onPhoto, onFermer, titre = 'Prendre une photo' }) => {
  const video = useRef(null);
  const [flux, setFlux] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [pret, setPret] = useState(false);

  useEffect(() => {
    if (!ouvert) return undefined;
    let arrete = false;
    let courant = null;
    setErreur(null);
    setPret(false);
    (async () => {
      try {
        courant = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1600 }, height: { ideal: 1200 } },
          audio: false
        });
        if (arrete) { courant.getTracks().forEach((p) => p.stop()); return; }
        setFlux(courant);
      } catch (e) {
        if (!arrete) setErreur(message(e));
      }
    })();
    return () => {
      arrete = true;
      if (courant) courant.getTracks().forEach((p) => p.stop());
      setFlux(null);
    };
  }, [ouvert]);

  // La vidéo n'existe qu'une fois la fenêtre montée : le flux s'y attache là.
  const attacher = (element) => {
    video.current = element;
    if (element && flux && element.srcObject !== flux) {
      element.srcObject = flux;
      element.play().then(() => setPret(true)).catch(() => {});
    }
  };

  const capturer = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      onPhoto(new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  return (
    <Dialog open={ouvert} onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Camera className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">{titre}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Cadrez l'actif en entier, puis capturez.</DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {erreur ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{erreur}</div>
          ) : (
            <div className="relative">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video ref={attacher} autoPlay playsInline muted className="max-h-[65vh] w-full rounded-lg bg-gray-950 object-contain" />
              {!pret && (
                <p className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-white/80">
                  <Loader2 className="h-4 w-4 animate-spin" /> Ouverture de la caméra…
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
          <Button size="default" type="button" className={`${BOUTON_CONTOUR} ${BOUTON_PIED}`} onClick={onFermer}>
            {erreur ? 'Fermer' : 'Annuler'}
          </Button>
          {!erreur && (
            <Button size="default" type="button" className={`${BOUTON_PIED} ${BOUTON_PLEIN}`} onClick={capturer} disabled={!pret}>
              <Camera className="h-4 w-4" />
              Capturer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FenetrePhotoCamera;
